/**
 * Reveal on scroll: section titles, cards and blocks fade in and rise 16px the first time
 * they enter the viewport (0.5 s, small stagger between siblings). Heroes are left alone so
 * their content shows immediately. The hidden state only exists once this script has added
 * its classes, so nothing stays hidden if JavaScript does not load; with
 * prefers-reduced-motion nothing is animated. After the animation the classes are removed so
 * hover transforms of the cards keep working.
 */

const TARGETS = [
  '.ab-section__head',
  '.ab-services__item',
  '.ab-pack',
  '.ab-formula',
  '.ab-formulas__option',
  '.ab-media-features__item',
  '.ab-steps__item',
  '.ab-explain__item',
  '.ab-faq__item',
  '.ab-checklist__intro',
  '.ab-checklist__body',
  '.ab-before-after__gallery',
  '.ab-before-after__video',
  '.ab-reviews__track',
  '.ab-gallery__item',
  '.ab-contact__card',
  '.ab-contact__map-col',
  '.ab-contact__steps',
  '.ab-cta__band',
  '.ab-packs__box',
  '.ab-social__inner',
  '.ab-logos__inner',
].join(',');

/** Elements never animated (visible immediately) */
const EXCLUDED = '.ab-hero, .ab-page-hero, [data-no-reveal]';

/** Stagger between siblings, in ms (capped) */
const STAGGER = 80;
const MAX_STAGGER = 4;

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/** @type {IntersectionObserver | undefined} */
let observer;

/** @param {IntersectionObserverEntry[]} entries */
function onIntersect(entries) {
  for (const entry of entries) {
    if (!entry.isIntersecting) continue;
    const element = /** @type {HTMLElement} */ (entry.target);
    observer?.unobserve(element);
    element.classList.add('is-revealed');
    element.addEventListener(
      'transitionend',
      () => {
        element.classList.remove('ab-reveal', 'is-revealed');
        element.style.removeProperty('--ab-reveal-delay');
      },
      { once: true }
    );
  }
}

/** @param {ParentNode} root */
function scan(root) {
  if (reducedMotion.matches) return;
  observer ??= new IntersectionObserver(onIntersect, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });

  /** @type {Map<Element, number>} */
  const siblingIndex = new Map();

  for (const element of root.querySelectorAll(TARGETS)) {
    if (!(element instanceof HTMLElement) || element.closest(EXCLUDED) || element.dataset.revealed) continue;
    element.dataset.revealed = 'true';

    // Stagger cards that share a parent (grid / list)
    const parent = element.parentElement;
    const index = parent ? siblingIndex.get(parent) ?? 0 : 0;
    if (parent) siblingIndex.set(parent, index + 1);
    element.style.setProperty('--ab-reveal-delay', `${Math.min(index, MAX_STAGGER) * STAGGER}ms`);

    element.classList.add('ab-reveal');
    observer.observe(element);
  }
}

scan(document);

// Theme editor: sections re-rendered in place
document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) scan(event.target);
});
